// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/ParkingSpotsTab.jsx
// *******************************************************************************
// PARKING SPOTS TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Table, Button, Flex, Popconfirm, Modal, Form, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const TYPE_OPTIONS = [
  { value: 'covered', label: translateX('covered') },
  { value: 'uncovered', label: translateX('uncovered') },
  { value: 'garage_box', label: translateX('garage_box') },
];

const STATUS_OPTIONS = [
  { value: 'available', label: translateX('available') },
  { value: 'occupied', label: translateX('occupied') },
  { value: 'rented', label: translateX('rented') },
  { value: 'sold', label: translateX('sold') },
];

const TYPE_COLORS = {
  covered: 'green',
  uncovered: 'orange',
  garage_box: 'blue',
};

const STATUS_COLORS = {
  available: 'green',
  occupied: 'red',
  rented: 'purple',
  sold: 'orange',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ParkingSpotsTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, _id, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spots, setSpots] = useState([]);
  const [spotsLoaded, setSpotsLoaded] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!data?.parkingSpotIds?.length) {
        setSpots([]);
        setSpotsLoaded(true);
        return;
      }
      const promises = data.parkingSpotIds.map(id =>
        axiosService.get({ url: `/v1/parking-spots/${id}` }).catch(() => [null])
      );
      const results = await Promise.all(promises);
      setSpots(results.map(r => r?.[0]).filter(Boolean));
      setSpotsLoaded(true);
    };
    load();
  }, [data?.parkingSpotIds]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    form.resetFields();
    setShowModal(true);
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const [spot] = await axiosService.post({
        url: '/v1/parking-spots',
        data: { ...values, unitId: _id },
      });

      if (spot?._id) {
        const updatedIds = [...(data?.parkingSpotIds || []), spot._id];
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { parkingSpotIds: updatedIds },
        });
        refresh?.();
        setShowModal(false);
        appService.message({ flag: 's', text: 'saved_successfully' });
      }
    } catch (error) {
      if (!error?.errorFields) {
        appService.message({ flag: 'e', text: 'error_saving' });
      }
    } finally {
      setLoading(false);
    }
  }, [form, data?.parkingSpotIds, _id, refresh]);

  const handleRemove = useCallback(
    async spotId => {
      setLoading(true);
      try {
        const updatedIds = (data?.parkingSpotIds || []).filter(id => id !== spotId);
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { parkingSpotIds: updatedIds },
        });
        await axiosService.delete({ url: '/v1/parking-spots', _id: spotId });
        refresh?.();
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?.parkingSpotIds, _id, refresh]
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('spot_number'),
      dataIndex: ['number'],
      render: text => text || '-',
    },
    {
      title: translateX('floor'),
      dataIndex: ['floor'],
      width: 120,
      render: text => text || '-',
    },
    {
      title: translateX('type'),
      dataIndex: ['type'],
      width: 140,
      render: text => (
        <Tag color={TYPE_COLORS[text] || 'default'}>{translateX(text) || '-'}</Tag>
      ),
    },
    {
      title: translateX('status'),
      dataIndex: ['status'],
      width: 140,
      render: text => (
        <Tag color={STATUS_COLORS[text] || 'default'}>{translateX(text) || '-'}</Tag>
      ),
    },
    {
      title: translateX('actions'),
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Popconfirm title={translateX('confirm_delete')} onConfirm={() => handleRemove(record._id)}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={loading}
          />
        </Popconfirm>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={spots}
        pagination={false}
        loading={loading || !spotsLoaded}
        size="small"
      />

      <Flex justify="end" className="mt-3">
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_parking_spot')}
        </Button>
      </Flex>

      <Modal
        title={translateX('add_parking_spot')}
        open={showModal}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={loading}
        destroyOnHidden
        width={480}
      >
        <Form form={form} layout="vertical" className="mt-3">
          <SimpleInput
            name="number"
            label="spot_number"
            required
          />
          <SimpleInput
            name="floor"
            label="floor"
          />
          <SimpleSelect
            name="type"
            label="type"
            options={TYPE_OPTIONS}
          />
          <SimpleSelect
            name="status"
            label="status"
            options={STATUS_OPTIONS}
          />
          <SimpleTextArea
            name="observation"
            label="observation"
          />
        </Form>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ParkingSpotsTab;
